import { difference } from "lodash";
import {
  CallingMethodLabels,
  CodecProtocol,
  CodecProtocolLabels,
  Device,
  ManagementProtocol,
  MonitoringLevel,
  MonitoringSource,
} from "@services/devices/types";
import {
  getAllRequiredFields,
  isRequiredField,
  propertyMapped,
} from "./isRequired";
import {
  fieldIsEmpty,
  allowedCallingMethod,
  deepDeviceGet,
  RequiredKeysOfDevice,
  isSimpleAsset,
  KeyofDevice,
} from "./const";
import { KeyofLocation } from "@pages/Assets/LocationProvisioning/const";
import { KeyofRoom } from "@pages/Assets/RoomProvisioning/const";
import { GenericFieldError } from "@common/validation/entityValidationUtils";
import { getDefaultMinMaxDates } from "@common/utils/localTime.utils";
import { getAllFieldsInError as locationGetAllFieldsInError } from "@common/utils/locations/validation";
import { getAllFieldsInError as roomGetAllFieldsInError } from "@common/utils/rooms/validation";
import { DeviceModelDetail } from "@services/catalog/types";
import { getMonitoringLevelValue } from "../monitoringLevel";
import {
  addDevicesSourceFilter,
  devicesSourceFilter,
} from "@pages/Dashboard/WidgetEdit/FilterPanel/constants";
import { deviceSourceFilterMap } from "@common/utils/filters/mappers/devices";
import { getMonitoringSourceValue } from "../monitoringSource";
import { Account } from "@services/account/types";
import {
  hasAggregatorSupportLicense,
  hasControllerSupportLicense,
  hasDMCASupportLicense,
  hasExpiredLicense,
  hasMultipleSourceSupportLicense,
} from "@common/utils/user";

export const devicesSourcesFilteredByLicense = <T>(
  devicesSourcesList: Filter<T>,
  account: Account | undefined
): Filter<T> => {
  if (!account) return devicesSourcesList;

  const hasControllerLicense = hasControllerSupportLicense(account);
  const hasDMCALicense = hasDMCASupportLicense(account);
  const hasAggregatorLicense = hasAggregatorSupportLicense(account);
  const hasMultipleSourceLicense = hasMultipleSourceSupportLicense(account);

  const excludedDeviceSources: string[] = [];

  if (hasExpiredLicense(account))
    return devicesSourcesList.filter(
      (deviceSource) => deviceSource.value === "Symphony Direct"
    );

  if (!hasControllerLicense) {
    excludedDeviceSources.push(
      "Room Controller",
      "Symphony Direct & Room Controller"
    );
  }

  if (!hasDMCALicense) {
    excludedDeviceSources.push(
      "DMCA",
      "Symphony Direct & DMCA",
      "3rd Party Management & DMCA"
    );
  }
  if (!hasAggregatorLicense) {
    excludedDeviceSources.push(
      "3rd Party Management",
      "Symphony Direct & 3rd Party Management",
      "3rd Party Management & DMCA"
    );
  }
  if (!hasMultipleSourceLicense) {
    excludedDeviceSources.push(
      "Symphony Direct & Room Controller",
      "Symphony Direct & 3rd Party Management",
      "Symphony Direct & DMCA",
      "3rd Party Management & DMCA"
    );
  }

  return devicesSourcesList.filter((deviceSource) => {
    const sourceValue = deviceSource.value as string;
    return !excludedDeviceSources.some((excluded) =>
      sourceValue.includes(excluded)
    );
  });
};

export const mapDeviceSourceToKeys = (
  monitoringSourceValues: string[]
): string[] => {
  const deviceSourceKey = Object.keys(
    deviceSourceFilterMap
  ) as DevicesSourceFilter[];
  return monitoringSourceValues.map(
    (monitoringSourceValue) =>
      deviceSourceKey.find((key) =>
        deviceSourceFilterMap[key].includes(monitoringSourceValue)
      ) || monitoringSourceValue
  );
};

export const getFilteredDeviceSourceValues = (
  account: Account | undefined,
  deviceSource: Filter<DevicesSourceFilter>,
  monitoringSourceValues: string[]
): DevicesSourceFilter[] => {
  return devicesSourcesFilteredByLicense(deviceSource, account)
    .filter(({ value }) =>
      mapDeviceSourceToKeys(monitoringSourceValues).includes(value)
    )
    .map(({ value }) => value);
};
export const isMonitoringLevelValid = (device: Device) =>
  !isSimpleAsset(device) ||
  (isSimpleAsset(device) && device.monitoringLevel === MonitoringLevel.None);

export const isMonitoringSourceValid = (device: Device) => {
  switch (device.monitoringLevel) {
    case MonitoringLevel.All:
      if (!fieldIsEmpty(device.monitoringSources)) {
        const deviceSourceValues = getFilteredDeviceSourceValues(
          device.room?.location?.account,
          devicesSourceFilter,
          device.monitoringSources as string[]
        );

        const deviceSourcesList = devicesSourcesFilteredByLicense(
          addDevicesSourceFilter,
          device.room?.location?.account
        );

        const monitoringSourceLabels = getMonitoringSourceValue(
          device?.monitoringSources
        ).map((item) => item.toLowerCase());

        const deviceMonitoringSourceValue = addDevicesSourceFilter.find((d) => {
          const labelParts = d.label.toLowerCase().split(" & ");

          return (
            difference(labelParts, monitoringSourceLabels).length === 0 &&
            difference(monitoringSourceLabels, labelParts).length === 0
          );
        })?.value;

        return (
          deviceSourceValues.length > 0 &&
          deviceSourceValues.every((value) =>
            deviceSourcesList.some((item) => item.value === value)
          ) &&
          Boolean(
            deviceSourcesList.find(
              (deviceSource) =>
                deviceSource.value === deviceMonitoringSourceValue
            )
          )
        );
      }
      return false;
    case MonitoringLevel.Ping:
      return (
        (device.monitoringSources?.length ?? 0) === 1 &&
        device.monitoringSources?.includes(MonitoringSource.CommProxy)
      );

    case MonitoringLevel.None:
      return true;
    default:
      return false;
  }
};

export const getExpirationDateErrors = (device: Device): FieldError[] => {
  const { minDate, maxDate } = getDefaultMinMaxDates;
  let temp: FieldError[] = [];
  if (
    device.details?.leaseExpirationDate !== undefined &&
    (device.details.leaseExpirationDate < minDate.getTime() ||
      device.details.leaseExpirationDate > maxDate.getTime() ||
      isNaN(device?.details?.leaseExpirationDate))
  ) {
    temp.push({
      property: "details.leaseExpirationDate",
      error: DevicePropertyErrors.EXPIRATION_DATE,
    });
  }
  if (
    device.details?.warrantyExpirationDate !== undefined &&
    (device.details.warrantyExpirationDate < minDate.getTime() ||
      device.details.warrantyExpirationDate > maxDate.getTime() ||
      isNaN(device.details.warrantyExpirationDate))
  ) {
    temp.push({
      property: "details.warrantyExpirationDate",
      error: DevicePropertyErrors.EXPIRATION_DATE,
    });
  }

  return temp;
};

export const getMonitoringLevelErrors = (device: Device): FieldError[] => {
  const monitoringLevelValue = getMonitoringLevelValue(
    device.monitoringLevel as unknown as string
  );
  if (isSimpleAsset(device) && monitoringLevelValue !== MonitoringLevel.None)
    return [
      {
        property: "monitoringLevel",
        error: DevicePropertyErrors.MONITORED_SIMPLE_ASSET,
      },
    ];
  if (
    monitoringLevelValue !== undefined &&
    ![MonitoringLevel.All, MonitoringLevel.None, MonitoringLevel.Ping].includes(
      monitoringLevelValue
    )
  )
    return [
      {
        property: "monitoringLevel",
        error: DevicePropertyErrors.INVALID_OPTION,
      },
    ];
  return [];
};
export const getMonitoringSourceErrors = (device: Device): FieldError[] => {
  const property = "monitoringSources";
  const accountId = device.room?.location?.account?.id;
  switch (device.monitoringLevel) {
    case MonitoringLevel.Ping:
      if (
        (device.monitoringSources?.length ?? 0) !== 1 &&
        device.monitoringSources?.[0] !== MonitoringSource.CommProxy
      )
        return [
          {
            property,
            error: DevicePropertyErrors.MONITORING_SOURCE_SHOULD_BE_COMMPROXY,
          },
        ];
    case MonitoringLevel.All:
      if (fieldIsEmpty(device.monitoringSources))
        return [
          { property, error: DevicePropertyErrors.MONITORING_SOURCE_EMPTY },
        ];
      if (accountId) {
        const monitoringSourceOptions = devicesSourcesFilteredByLicense(
          addDevicesSourceFilter,
          device.room?.location?.account
        );

        const monitoringSourceValues = getMonitoringSourceValue(
          device.monitoringSources
        ).map((item) => item.toLowerCase());

        if (
          !monitoringSourceOptions.some((option) => {
            const optionParts = option.label.toLowerCase().split(" & ");

            if (optionParts.length !== monitoringSourceValues.length) {
              return false;
            }

            return (
              difference(optionParts, monitoringSourceValues).length === 0 &&
              difference(monitoringSourceValues, optionParts).length === 0
            );
          })
        ) {
          return [
            {
              property,
              error: DevicePropertyErrors.INVALID_OPTION,
            },
          ];
        }
      }
    case MonitoringLevel.None:
    default:
      return [];
  }
};

export const isProtocolValid = (device: Device) =>
  getProtocolErrors(device).length === 0;

export const getProtocolErrors = (device: Device): FieldError[] => {
  const errors: FieldError[] = [];
  if (
    device.callingMethod &&
    !Object.keys(CallingMethodLabels).includes(device.callingMethod)
  )
    errors.push({
      property: "callingMethod",
      error: DevicePropertyErrors.INVALID_OPTION,
    });

  if (
    device.protocol &&
    !Object.keys(CodecProtocolLabels).includes(device.protocol)
  )
    errors.push({
      property: "protocol",
      error: DevicePropertyErrors.INVALID_OPTION,
    });
  if (device.callCapable) {
    if (device.protocol === CodecProtocol.None)
      errors.push({
        property: "protocol",
        error: DevicePropertyErrors.PROTOCOL_CANNOT_BE_NONE,
      });
    if (device.protocol === undefined)
      errors.push({
        property: "protocol",
        error: DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY,
      });
    else if (allowedCallingMethod[device.protocol] === undefined)
      errors.push({
        property: "protocol",
        error: DevicePropertyErrors.UNKNOWN_PROTOCOL,
      });
    else if (device.callingMethod === undefined)
      errors.push({
        property: "callingMethod",
        error: DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY,
      });
    else if (
      !allowedCallingMethod[device.protocol]?.includes(device.callingMethod)
    )
      errors.push({
        property: "callingMethod",
        error: DevicePropertyErrors.CALLING_METHOD_MISMATCH,
      });
  }
  return errors;
};

export const isPositionValid = (device: Device) => {
  if (!isRequiredField(device, "details.position")) return true;
  return !isNaN(device.details?.position ?? NaN);
};
export const getPositionErrors = (device: Device): FieldError[] => {
  if (!isRequiredField(device, "details.position")) return [];
  return isPositionValid(device)
    ? []
    : [
        {
          property: "details.position",
          error: DevicePropertyErrors.INVALID_NUMBER,
        },
      ];
};

export const areManagementInterfacesValid = (device: Device) =>
  device.details?.managementInterfaces?.every(
    ({ localPort }) => !isNaN(!!localPort ? localPort : NaN)
  );

export const getManagementInterfaceErrors = (device: Device): FieldError[] => {
  const localPortArray = device.details?.managementInterfaces?.map(
    (managementInterface) => managementInterface.localPort
  );

  if (
    device?.monitoringSources?.includes(MonitoringSource.CommProxy) &&
    device?.monitoringLevel === MonitoringLevel.All &&
    !isAtLeastOneFilled(localPortArray ?? [])
  ) {
    return (
      device.details?.managementInterfaces?.map(({ protocol }) => ({
        property: `details.managementInterfaces.${
          protocol as ManagementProtocol
        }.localPort`,
        error: DevicePropertyErrors.MANAGEMENT_INTERFACE_REQUIRED,
      })) ?? []
    );
  }

  return (
    device?.details?.managementInterfaces
      ?.filter((managementInterface) => {
        const { localPort } = managementInterface;
        /**
         * The field `protocol` is only used to discriminate between the interfaces, so we don't care if it's filled
         * for that particular error
         * We're only checking if there's any information filled for it (a username, password, or anything else)
         * without the local port being provided for it, as the port is required in the case the interface is used.
         * */
        return (
          fieldIsEmpty(localPort) &&
          Object.entries(managementInterface).filter(
            ([key, val]) =>
              key !== "protocol" && key !== "secure" && !fieldIsEmpty(val)
          ).length > 0
        );
      })
      .map(({ protocol }) => ({
        property: `details.managementInterfaces.${
          protocol as ManagementProtocol
        }.localPort`,
        error: DevicePropertyErrors.INTERFACE_LOCAL_PORT_IS_EMPTY,
      })) ?? []
  );
};

export const getCatalogValuesErrors = (
  device: Device,
  catalog?: DeviceModelDetail[]
): FieldError[] => {
  const errors: FieldError[] = [];
  if (catalog === undefined) return errors;
  if (
    (device.type ?? "") !== "" &&
    !catalog.find((x) => x.deviceType === device.type)
  )
    errors.push({
      property: "type",
      error: DevicePropertyErrors.INVALID_OPTION,
    });
  if (
    (device.category ?? "") !== "" &&
    !catalog.find((x) => x.category === device.category)
  )
    errors.push({
      property: "category",
      error: DevicePropertyErrors.INVALID_OPTION,
    });
  if (
    (device.manufacturer ?? "") !== "" &&
    !catalog.find((x) => x.manufacturer === device.manufacturer)
  )
    errors.push({
      property: "model",
      error: DevicePropertyErrors.INVALID_OPTION,
    });
  if ((device.model ?? "") !== "") {
    let model = catalog.find((x) => x.model === device.model);
    if (model === undefined)
      errors.push({
        property: "model",
        error: DevicePropertyErrors.INVALID_OPTION,
      });
    else if (
      device.model !== "Unknown" &&
      (model.deviceType !== device.type ||
        model.manufacturer !== device.manufacturer ||
        model.category !== device.category)
    )
      errors.push({
        property: "model",
        error: DevicePropertyErrors.MODEL_MISMATCH,
      });
  }
  return errors;
};

export const areCatalogValuesValid = (
  device: Partial<Device>,
  modelDatas?: Partial<DeviceModelDetail>,
  catalog?: DeviceModelDetail[]
) => getCatalogValuesErrors(device as Device, catalog).length === 0;

const isDeviceNameValid = (device: Partial<Device>) =>
  !isSimpleAsset(device) ||
  (isSimpleAsset(device) && Boolean(device.name?.trim()));
export const isAtLeastOneFilled = (arr: Array<any>) =>
  arr.length === 0 || arr.some((x) => !fieldIsEmpty(x));

const testOneKey = (
  device: Partial<Device>,
  key: RequiredKeysOfDevice[number]
) => {
  return Array.isArray(key)
    ? isAtLeastOneFilled(key.map((key) => deepDeviceGet(device, key)))
    : !fieldIsEmpty(deepDeviceGet(device, key));
};

export const getErrorFromLocationFields = (
  keys: KeyofLocation[]
): KeyofDevice[] => keys.map((k) => `room.location.${k}` as KeyofDevice);

export const getErrorFromRoomFields = (keys: KeyofRoom[]): KeyofDevice[] =>
  keys.map((k) => `room.${k}` as KeyofDevice);

export const getEmptyRequiredFields = (
  device: Partial<Device>,
  includeLocationFields: boolean = false,
  includeRoomFields: boolean = false,
  modelDatas: Partial<DeviceModelDetail> = {}
): FieldError[] =>
  getAllRequiredFields(
    device,
    includeLocationFields,
    includeRoomFields,
    modelDatas
  )
    .filter((x) => !testOneKey(device, x))
    .map((property) => ({
      property,
      error: DevicePropertyErrors.REQUIRED_FIELD_IS_EMPTY,
    }));
export const areRequiredFieldFilled = (
  device: Partial<Device>,
  modelDatas?: Partial<DeviceModelDetail>
) => {
  return getEmptyRequiredFields(device, false, false, modelDatas).length === 0;
};

const isValidIpv4Address = (ipAddress: string | undefined): boolean => {
  const ipv4AddressRegex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/i;
  return ipAddress ? ipv4AddressRegex.test(ipAddress) : false;
};

const isValidIpv6Address = (ipAddress: string | undefined): boolean => {
  const ipv6AddressRegex =
    /^(([0-9A-Fa-f]{1,4}:){7})([0-9A-Fa-f]{1,4})$|(([0-9A-Fa-f]{1,4}:){1,6}:)(([0-9A-Fa-f]{1,4}:){0,4})([0-9A-Fa-f]{1,4})$/i;
  return ipAddress ? ipv6AddressRegex.test(ipAddress) : false;
};

const isValidIpAddress = (ipAddress: string | undefined): boolean =>
  isValidIpv4Address(ipAddress) || isValidIpv6Address(ipAddress);

const isValidMACAddress = (macAddress: string | undefined): boolean => {
  if (macAddress === undefined) return false;
  const macRegex = /^[a-fA-F0-9]{2}([-:])([a-fA-F0-9]{2}\1){4}[a-fA-F0-9]{2}$/i;
  return macRegex.test(macAddress);
};

const isValidUrl = (url: string | undefined): boolean => {
  const urlRegex =
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/i;
  return url ? urlRegex.test(url) : false;
};

export const getManagementAddressError = (
  value: string,
  key: KeyofDevice
): boolean => {
  if (!value) return false;
  switch (key) {
    case "ipAddress":
    case "details.managementUrl":
      return !(isValidIpAddress(value) || isValidUrl(value));
    case "localIpAddress":
      return !isValidIpAddress(value);
    case "macAddress":
      return !isValidMACAddress(value);
    default:
      return false;
  }
};
export const getManagementAddressesError = (
  device: Partial<Device>,
  modelDatas?: Partial<DeviceModelDetail>
) => {
  const errors: FieldError[] = [];
  if (
    !propertyMapped("ipAddress", modelDatas) &&
    device.ipAddress &&
    getManagementAddressError(device.ipAddress, "ipAddress")
  ) {
    errors.push({
      property: "ipAddress",
      error: DevicePropertyErrors.MALFORMED_ADDRESS,
    });
  }
  if (
    !propertyMapped("localIpAddress", modelDatas) &&
    device.localIpAddress &&
    getManagementAddressError(device.localIpAddress, "localIpAddress")
  ) {
    errors.push({
      property: "localIpAddress",
      error: DevicePropertyErrors.MALFORMED_ADDRESS,
    });
  }
  if (
    !propertyMapped("details.managementUrl", modelDatas) &&
    device?.details?.managementUrl &&
    getManagementAddressError(
      device.details.managementUrl,
      "details.managementUrl"
    )
  ) {
    errors.push({
      property: "details.managementUrl",
      error: DevicePropertyErrors.MALFORMED_ADDRESS,
    });
  }
  if (
    !propertyMapped("macAddress", modelDatas) &&
    device?.macAddress &&
    getManagementAddressError(device.macAddress, "macAddress")
  ) {
    errors.push({
      property: "macAddress",
      error: DevicePropertyErrors.MALFORMED_ADDRESS,
    });
  }
  return errors;
};

export const areManagementAddressesValid = (
  device: Partial<Device>,
  modelDatas: Partial<DeviceModelDetail> | undefined
) => {
  return getManagementAddressesError(device, modelDatas).length === 0;
};

export enum DevicePropertyErrors {
  REQUIRED_FIELD_IS_EMPTY = "This field is required.",
  INVALID_OPTION = "Please select a valid option.",
  MODEL_MISMATCH = "The selected type, category, manufacturer and model do not match.",
  INTERFACE_LOCAL_PORT_IS_EMPTY = "Interfaces must have a local port.",
  INVALID_NUMBER = "This field must contain a valid number.",
  MONITORING_SOURCE_EMPTY = "A monitoring source is required for devices of this monitoring level.",
  MONITORING_SOURCE_SHOULD_BE_COMMPROXY = `The monitoring source for this device's monitoring level should be "CommProxy".`,
  PROTOCOL_CANNOT_BE_NONE = "The protocol can't be set to none.",
  CALLING_METHOD_MISMATCH = "This calling method is not available for the chosen protocol.",
  UNKNOWN_PROTOCOL = "Unknown protocol",
  MONITORED_SIMPLE_ASSET = "The simple asset can not be monitored",
  EXPIRATION_DATE = "Please select a valid date.",
  ROOM_ID = "The Room ID should match the Room Name",
  INVALID_LONGITUDE = "The longitude is invalid.",
  INVALID_LATITUDE = "The latitude is invalid.",
  MANAGEMENT_INTERFACE_REQUIRED = "At least one Device Management Interface required while Monitoring Source is Symphony Direct and Monitoring Service is Advanced Monitoring",
  MALFORMED_ADDRESS = "The address is malformed",
}
export type FieldError = GenericFieldError<
  RequiredKeysOfDevice[number],
  DevicePropertyErrors
>;

export const getAllFieldsInError = (
  device: Partial<Device>,
  includeLocationFields: boolean = false,
  includeRoomFields: boolean = false,
  catalog?: DeviceModelDetail[],
  roomTypesValues?: {
    id: string;
    name: string;
  }[]
): FieldError[] => {
  return [
    getEmptyRequiredFields(device, includeLocationFields, includeRoomFields),
    getManagementInterfaceErrors(device),
    getPositionErrors(device),
    getMonitoringLevelErrors(device),
    getMonitoringSourceErrors(device),
    getProtocolErrors(device),
    getExpirationDateErrors(device),
    getCatalogValuesErrors(device, catalog),
    getManagementAddressesError(device),
    includeRoomFields
      ? roomGetAllFieldsInError(device.room ?? {}, false, roomTypesValues).map(
          ({ property, error }) =>
            ({
              property: `room.${property}`,
              error: error as unknown as DevicePropertyErrors,
            } as FieldError)
        )
      : [],
    includeLocationFields
      ? locationGetAllFieldsInError(device?.room?.location ?? {}).map(
          ({ property, error }) =>
            ({
              property: `room.location.${property}`,
              error: error as unknown as DevicePropertyErrors,
            } as FieldError)
        )
      : [],
  ].flat();
};

export const isDeviceValid = (
  device: Partial<Device>,
  modelDatas?: Partial<DeviceModelDetail>,
  catalog?: DeviceModelDetail[]
) =>
  [
    areRequiredFieldFilled,
    isMonitoringLevelValid,
    isMonitoringSourceValid,
    isProtocolValid,
    areManagementInterfacesValid,
    isPositionValid,
    areCatalogValuesValid,
    isDeviceNameValid,
    areManagementAddressesValid,
  ].every((fn) => fn(device, modelDatas, catalog));
